import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
} from '@apollo/orbit';
import { Telescope } from '@apollo/space-kit/illustrations/Telescope';
import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect, useState } from 'react';

import { Form } from 'src/components/common/form/Form';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  CheckWorkflowStatus,
  UpsertContractMutation,
  UpsertContractMutationVariables,
} from 'src/lib/graphqlTypes/types';
import { useLDFlag } from 'src/lib/launchDarkly/hooks';

import { ContractConfigHeader } from '../ContractConfigHeader';
import { ContractConfigStep, ContractConfigWorkflow } from '../steps';

import { ChecksConfirmationModal } from './ChecksConfirmationModal';
import { CheckWorkflowStatusWidgetFragment } from './CheckStatusWidget';
import { ContractSchemaDiffViewer } from './ContractSchemaDiffViewer';
import { SchemaDiffMetaData } from './SchemaDiffMetaData';

export interface ContractConfigReviewModalState {
  graphId?: string;
  graphTitle?: string;
  contractVariant?: string;
  sourceVariant?: string;
  includedTags?: Set<string>;
  excludedTags?: Set<string>;
  hideUnreachableTypes?: boolean;
  initiateLaunch?: boolean;
  sourceSchema?: SchemaDiffMetaData;
  proposedSchema?: SchemaDiffMetaData;
  latestSchema?: SchemaDiffMetaData;
  errors?: string[];
  isNoOpPreview?: boolean;
  checkWorkflowId?: string;
}

const upsertContractMutation = gql<
  UpsertContractMutation,
  UpsertContractMutationVariables
>`
  mutation UpsertContractMutation(
    $graphId: ID!
    $sourceVariant: String!
    $contractVariantName: String!
    $filterConfig: FilterConfigInput!
    $initiateLaunch: Boolean!
  ) {
    service(id: $graphId) {
      upsertContractVariant(
        contractVariantName: $contractVariantName
        filterConfig: $filterConfig
        sourceVariant: $sourceVariant
        initiateLaunch: $initiateLaunch
      ) {
        ... on ContractVariantUpsertSuccess {
          contractVariant {
            id
          }
        }
        ... on ContractVariantUpsertErrors {
          errorMessages
        }
      }
    }
  }
`;

export const CheckWorkflowStatusQuery = gql`
  query CheckWorkflowStatusQuery(
    $graphId: ID!
    $checkWorkflowId: ID!
    $proposalsUseHasWarnings: Boolean!
  ) {
    service(id: $graphId) {
      id
      checkWorkflow(id: $checkWorkflowId) {
        id
        baseVariant {
          id
          sourceVariant {
            id
          }
        }
        ...CheckWorkflowStatusWidgetFragment
      }
    }
  }
  ${CheckWorkflowStatusWidgetFragment}
`;

interface Props {
  isOpen: boolean;
  state: ContractConfigReviewModalState | null;
  onInitiateLaunchChange: (initiateLaunch: boolean) => void;
  onPrevious: () => void;
  onClose: () => void;
  onComplete: () => void;
  workflow: ContractConfigWorkflow;
}

export const ContractConfigReviewModal = ({
  isOpen,
  state,
  onInitiateLaunchChange,
  onPrevious,
  onClose,
  onComplete,
  workflow,
}: Props) => {
  if (!state?.graphId || !state?.sourceVariant || !state?.contractVariant) {
    throw new Error('Not enough information to load modal.');
  }
  const proposalsUseHasWarnings = useLDFlag(
    'tooling-proposals-use-has-warnings',
  );

  // Query details about the check workflow for the proposed contract schema.
  // On creates, this query is skipped.
  const {
    data: checkWorkflowData,
    loading: checkWorkflowDataLoading,
    error: checkWorkflowError,
    stopPolling,
  } = useQuery<
    GraphQLTypes.CheckWorkflowStatusQuery,
    GraphQLTypes.CheckWorkflowStatusQueryVariables
  >(CheckWorkflowStatusQuery, {
    variables: {
      graphId: state.graphId,
      checkWorkflowId: state.checkWorkflowId as string,
      proposalsUseHasWarnings,
    },
    skip:
      workflow === ContractConfigWorkflow.create ||
      state.checkWorkflowId === undefined,
    pollInterval: 2000,
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  if (checkWorkflowError) Sentry.captureException(checkWorkflowError);

  useEffect(() => {
    if (
      checkWorkflowData?.service?.checkWorkflow &&
      checkWorkflowData.service.checkWorkflow.status !==
        CheckWorkflowStatus.PENDING
    ) {
      stopPolling();
    }
  }, [checkWorkflowData, stopPolling]);

  const client = useApolloClient();
  const [upsertContract, { data, loading, error }] = useMutation(
    upsertContractMutation,
    {
      onCompleted() {
        client.cache.evict({
          id: client.cache.identify({
            __typename: data?.service?.__typename,
            id: state?.graphId,
          }),
        });
      },
    },
  );

  const onMaybeClose = useCallback(() => {
    if (loading) return;
    onClose();
  }, [onClose, loading]);

  const onSubmit = useCallback(async () => {
    if (!state?.graphId || !state?.sourceVariant || !state?.contractVariant) {
      throw new Error('Not enough information to load modal.');
    }

    try {
      const { data: responseData } = await upsertContract({
        variables: {
          graphId: state?.graphId,
          contractVariantName: state?.contractVariant,
          sourceVariant: state?.sourceVariant,
          filterConfig: {
            include: Array.from(state?.includedTags ?? []),
            exclude: Array.from(state?.excludedTags ?? []),
            hideUnreachableTypes: Boolean(state?.hideUnreachableTypes),
          },
          initiateLaunch: Boolean(state?.initiateLaunch),
        },
        update: (cache) => {
          cache.evict({
            id: cache.identify({ __typename: 'Service', id: state.graphId }),
            fieldName: 'variant',
          });
        },
      });
      if (
        responseData?.service?.upsertContractVariant.__typename ===
        'ContractVariantUpsertSuccess'
      ) {
        // go to the next step
        onComplete();
      } else if (responseData?.service?.upsertContractVariant.errorMessages) {
        Sentry.captureMessage(
          responseData?.service?.upsertContractVariant.errorMessages.join(', '),
        );
      }
    } catch (e) {
      // already handled above
    }
  }, [state, upsertContract, onComplete]);

  useEffect(() => {
    onInitiateLaunchChange(!state?.isNoOpPreview);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errorUpsertingContract =
    data?.service?.upsertContractVariant.__typename ===
    'ContractVariantUpsertErrors';

  // A check workflow should only have been initiated for edits.
  // Since this component handles both create and edits, we may or may not
  // have a check workflow.
  const maybeCheckWorkflow = checkWorkflowData?.service?.checkWorkflow;
  const [showChecksConfirmationModal, setShowChecksConfirmationModal] =
    useState(false);

  return (
    <Modal isOpen={isOpen} size="2xl" onClose={onMaybeClose}>
      <ModalOverlay />
      <Form
        onSubmit={() => {
          if (
            checkWorkflowDataLoading ||
            checkWorkflowData?.service?.checkWorkflow?.status ===
              CheckWorkflowStatus.PENDING ||
            checkWorkflowData?.service?.checkWorkflow?.status ===
              CheckWorkflowStatus.FAILED
          ) {
            // If checks are pending or fail for the proposed schema, we want to warn the user
            // before they submit their changes. This promps the user to approve
            // the changes before continuing.
            setShowChecksConfirmationModal(true);
          } else {
            // Submit and go to next step
            onSubmit();
          }
        }}
        noValidate
      >
        <ModalContent>
          <ModalHeader>
            <ContractConfigHeader
              currentStep={ContractConfigStep.ReviewSchema}
              workflow={workflow}
            />
          </ModalHeader>
          <ModalBody>
            {error && (
              <ErrorMessage>
                Something went wrong{' '}
                {workflow === ContractConfigWorkflow.create
                  ? 'creating'
                  : 'updating'}{' '}
                your contract.
              </ErrorMessage>
            )}

            {data?.service?.upsertContractVariant.__typename ===
              'ContractVariantUpsertErrors' && (
              <ErrorMessage>
                {data.service.upsertContractVariant.errorMessages.map((e) => (
                  <p key={e}>{e}</p>
                ))}
              </ErrorMessage>
            )}

            {!errorUpsertingContract && (
              <>
                {state?.sourceSchema?.apiSchema &&
                state?.sourceSchema.supergraphSchema &&
                state?.proposedSchema?.apiSchema &&
                state?.proposedSchema.supergraphSchema ? (
                  <div className="-mt-2">
                    {/* Checks Confirmation Modal */}
                    {maybeCheckWorkflow && showChecksConfirmationModal && (
                      <ChecksConfirmationModal
                        isOpen
                        checkWorkflow={maybeCheckWorkflow}
                        onApproval={() => {
                          // Submit and go to next step
                          onSubmit();
                        }}
                        onClose={() => {
                          // Hide the confirmation modal
                          setShowChecksConfirmationModal(false);
                        }}
                      />
                    )}

                    {/* Schema Viewer */}
                    <ContractSchemaDiffViewer
                      source={{
                        graphRef: {
                          graphId: state.graphId as string,
                          graphVariant: state.sourceVariant as string,
                        },
                        schema: state.sourceSchema,
                      }}
                      contract={{
                        graphRef: {
                          graphId: state.graphId as string,
                          graphVariant: state.contractVariant as string,
                        },
                        latestSchema: state.latestSchema,
                        proposedSchema: state.proposedSchema,
                      }}
                      checkWorkflow={maybeCheckWorkflow}
                      checkWorkflowLoading={
                        state.checkWorkflowId !== undefined ||
                        checkWorkflowDataLoading
                      }
                    />

                    {/* Launch Approval Section */}
                    {workflow === ContractConfigWorkflow.edit && (
                      <section className="my-4 flex flex-col rounded border border-primary bg-primary-inverted px-6 py-2 text-base">
                        <Text className="font-semibold">
                          Initiate contract launch
                        </Text>
                        <div className="flex flex-row items-center justify-between">
                          <Text>
                            {state?.initiateLaunch
                              ? 'A launch will initiate, and changes to your contract schema will be published.'
                              : 'The contract configuration will be updated but a launch will not be initiated.'}
                          </Text>
                          <FormControl className="flex-1">
                            <FormLabel hidden>Initiate launch</FormLabel>
                            <Switch
                              isChecked={state?.initiateLaunch}
                              onChange={(e) =>
                                onInitiateLaunchChange(e.target.checked)
                              }
                              isDisabled={state?.isNoOpPreview}
                            />
                          </FormControl>
                        </div>
                      </section>
                    )}
                  </div>
                ) : (
                  // TODO: improve the messaging here depending on why we're not showing a diff...
                  // Invalid Schema? No changes? Easily fixable error?
                  <div className="my-10 flex h-80 flex-col items-center justify-center">
                    <Telescope className="mb-2" />
                    <div className="text-3xl font-semibold text-neutral">
                      Invalid Schema
                    </div>
                    <div className="text-secondary">
                      You can still{' '}
                      {workflow === ContractConfigWorkflow.create
                        ? 'create'
                        : 'update'}{' '}
                      your contract.
                    </div>
                  </div>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              type="button"
              isDisabled={loading}
              onClick={onClose}
            >
              Cancel
            </Button>
            <ButtonGroup>
              <Button
                variant="secondary"
                isDisabled={loading}
                onClick={onPrevious}
              >
                Back
              </Button>
              <Button
                variant={
                  workflow === ContractConfigWorkflow.edit &&
                  maybeCheckWorkflow?.status === CheckWorkflowStatus.FAILED
                    ? 'destructive'
                    : 'primary'
                }
                isLoading={loading}
                loadingText={
                  workflow === ContractConfigWorkflow.create
                    ? 'Create'
                    : 'Update'
                }
                type="submit"
                isDisabled={errorUpsertingContract}
              >
                {workflow === ContractConfigWorkflow.create
                  ? 'Create'
                  : 'Update'}
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
};
